// src/services/compliance.ts
/**
 * Compliance API service (Lex Koller / KYC).
 */
import api from './api'
import type {
  KycCallbackResponse,
  KycCaptureMode,
  KycCheckRun,
  KycHandoffResponse,
  KycReviewDecisionRequest,
  KycReviewFileSlot,
  KycReviewItem,
  KycSelfSlot,
  KycSelfStatus,
  KycSelfSubmitRequest,
  KycStartRequest,
  KycStartResponse,
  KycStatusResponse,
  RegulatedConfirmRequest,
  RegulatedConfirmResponse,
  RegulatedDeclarationRequest,
  RegulatedDeclarationResponse,
  RegulatedImportResponse,
  RegulatedMatch,
  RegulatedMatchListResponse,
  RegulatedMatchRunResponse,
  RegulatedMatchStatusValue,
  RegulatedStatusResponse,
} from '@/types/kyc'
import type {
  LexKollerAssessmentRequest,
  LexKollerAssessmentResponse,
  LexKollerInstitutionalDeclarationRequest,
  LexKollerInstitutionalDeclarationResponse,
  LexKollerStatusResponse,
} from '@/types/compliance'

/** Re-exported for existing importers — the definitions live in `@/types/compliance`. */
export type {
  EntityType,
  LexKollerAssessmentRequest,
  LexKollerAssessmentResponse,
  LexKollerData,
  LexKollerPrecheck,
  LexKollerStatusResponse,
  LexKollerStatusValue,
  NationalityCategory,
  ResidencePermit,
} from '@/types/compliance'

/**
 * Local "dialog dismissed" flag (STORY-989).
 *
 * The backend status only knows UNRESTRICTED | CONDITIONAL | RESTRICTED | UNKNOWN —
 * there is no "skipped" state and no dismiss endpoint. The auto-opening dialog is
 * therefore suppressed per user/device via localStorage; the assessment stays
 * reachable through the settings page.
 */
const dismissStorageKey = (userId: number) => `lex-koller-dismissed:${userId}`

/** True if the user already closed/skipped the auto-opened Lex Koller dialog. */
export function isLexKollerDismissed(userId: number): boolean {
  try {
    return !!localStorage.getItem(dismissStorageKey(userId))
  } catch {
    return false
  }
}

/** Remember that the dialog was dismissed so it does not open again. */
export function dismissLexKoller(userId: number): void {
  try {
    localStorage.setItem(dismissStorageKey(userId), new Date().toISOString())
  } catch {
    // Storage unavailable (e.g. private mode) — dialog may reappear, acceptable
  }
}

/** Clear the flag, e.g. after the assessment has been completed. */
export function clearLexKollerDismissed(userId: number): void {
  try {
    localStorage.removeItem(dismissStorageKey(userId))
  } catch {
    // Storage unavailable — nothing to clear
  }
}

/**
 * "Identification dialog dismissed" flag (STORY-990).
 *
 * The GwG dialog is deliberately soft: «Später» is allowed and the dialog
 * returns on the next login. It is therefore suppressed per user for the
 * current browser session only — sessionStorage, not localStorage.
 */
const kycIntroStorageKey = (userId: number) => `kyc-intro-dismissed:${userId}`

/** True if the user already skipped/closed the dialog in this session. */
export function isKycIntroDismissed(userId: number): boolean {
  try {
    return !!sessionStorage.getItem(kycIntroStorageKey(userId))
  } catch {
    return false
  }
}

/** Remember the skip for the remainder of this session. */
export function dismissKycIntro(userId: number): void {
  try {
    sessionStorage.setItem(kycIntroStorageKey(userId), new Date().toISOString())
  } catch {
    // Storage unavailable (e.g. private mode) — dialog may reappear, acceptable
  }
}

/**
 * "Compliance dialog already shown" flag (STORY-1000).
 *
 * Identification and Lex Koller are ONE dialog with two steps. Whether
 * something is still open changes while the dialog is on screen (an ident that
 * just succeeded clears `required`), so the auto-open must not be derived from
 * the open items alone — it would pop up a second time 1.5 s after closing.
 * The dialog is therefore offered exactly once per browser session.
 */
const complianceShownStorageKey = (userId: number) => `compliance-dialog-shown:${userId}`

/** True if the combined compliance dialog was already opened this session. */
export function isComplianceDialogShown(userId: number): boolean {
  try {
    return !!sessionStorage.getItem(complianceShownStorageKey(userId))
  } catch {
    return false
  }
}

/** Remember that the dialog was shown — it does not open again by itself. */
export function markComplianceDialogShown(userId: number): void {
  try {
    sessionStorage.setItem(complianceShownStorageKey(userId), new Date().toISOString())
  } catch {
    // Storage unavailable (e.g. private mode) — dialog may reappear, acceptable
  }
}

/**
 * "Came back from the ident provider" marker (STORY-991).
 *
 * Starting an identification navigates the tab away to the provider, so the
 * only thing that survives the round trip is storage. The marker makes the
 * dialog reopen on the check-run screen instead of the generic result text.
 */
const kycReturnStorageKey = (userId: number) => `kyc-ident-return:${userId}`

/** Remember that the user is being sent to the ident provider. */
export function markKycReturnPending(userId: number): void {
  try {
    sessionStorage.setItem(kycReturnStorageKey(userId), new Date().toISOString())
  } catch {
    // Storage unavailable — the user simply sees the plain status instead
  }
}

/** True exactly once after a return from the provider; clears the marker. */
export function consumeKycReturnPending(userId: number): boolean {
  try {
    const marker = sessionStorage.getItem(kycReturnStorageKey(userId))
    if (marker) sessionStorage.removeItem(kycReturnStorageKey(userId))
    return !!marker
  } catch {
    return false
  }
}

/**
 * Id of the last check run of a user (STORY-991).
 *
 * The API has no "list my runs" endpoint — it only resolves a run by id. The
 * settings page therefore remembers the last run per user so the customer can
 * look at what we checked, also after a reload.
 */
const kycLastRunStorageKey = (userId: number) => `kyc-last-run:${userId}`

export function rememberKycRunId(userId: number, runId: number): void {
  try {
    localStorage.setItem(kycLastRunStorageKey(userId), String(runId))
  } catch {
    // Storage unavailable — the run is simply not linked in the settings
  }
}

export function lastKycRunId(userId: number): number | null {
  try {
    const stored = Number(localStorage.getItem(kycLastRunStorageKey(userId)))
    return Number.isInteger(stored) && stored > 0 ? stored : null
  } catch {
    return null
  }
}

/**
 * Id of the self identification a user last started (STORY-1139).
 *
 * `GET /kyc/status` only reports the contact status — which identification is
 * behind a `PENDING` it does not say, and the list endpoint is staff-only. The
 * dialog therefore remembers the identification it started: it is what tells an
 * aborted capture («fortsetzen») from one that is already handed in («wird
 * geprüft»). localStorage, not sessionStorage — an abandoned identification is
 * usually picked up in a new browser session.
 */
const kycSelfIdentStorageKey = (userId: number) => `kyc-self-ident:${userId}`

export function rememberKycSelfIdentId(userId: number, verificationId: number): void {
  try {
    localStorage.setItem(kycSelfIdentStorageKey(userId), String(verificationId))
  } catch {
    // Storage unavailable — the dialog then offers to continue without knowing
  }
}

export function lastKycSelfIdentId(userId: number): number | null {
  try {
    const stored = Number(localStorage.getItem(kycSelfIdentStorageKey(userId)))
    return Number.isInteger(stored) && stored > 0 ? stored : null
  } catch {
    return null
  }
}

const complianceApi = {
  getLexKollerStatus: () =>
    api.get<LexKollerStatusResponse>('/api/compliance/lex-koller/status').then((r) => r.data),

  assessLexKoller: (data: LexKollerAssessmentRequest) =>
    api.post<LexKollerAssessmentResponse>('/api/compliance/lex-koller/assess', data).then((r) => r.data),

  /**
   * Self-declaration of a supervised/registered institution (Issue #1000).
   *
   * Confirms that the company is not foreign-controlled above one third
   * (Art. 5 BewG) — the assessment then ends in `NOT_APPLICABLE_INSTITUTIONAL`
   * without any nationality or permit questions.
   */
  declareLexKollerInstitutional: (data: LexKollerInstitutionalDeclarationRequest) =>
    api
      .post<LexKollerInstitutionalDeclarationResponse>(
        '/api/compliance/lex-koller/declare-institutional',
        data,
      )
      .then((r) => r.data),

  /**
   * GwG status of the current user (contact + company + "required" flag).
   *
   * Also carries the open self identification as `self_ident` (api#1042) — the
   * dialog reads «offen» vs. «eingereicht» from there instead of the browser.
   */
  getKycStatus: () =>
    api.get<KycStatusResponse>('/api/compliance/kyc/status').then((r) => r.data),

  /** Starts an identification session; the caller redirects to `session_url`. */
  startKyc: (data: KycStartRequest) =>
    api.post<KycStartResponse>('/api/compliance/kyc/start', data).then((r) => r.data),

  /**
   * Stores one file of a self identification (Issue #1070).
   *
   * `slot` is `front`, `back`, `selfie` or `pdf`; `mode` tells the reviewer
   * whether the picture was taken live or uploaded. 413 = too large,
   * 415 = wrong type, 409 = already submitted.
   */
  uploadSelfIdentFile: (
    verificationId: number,
    slot: KycSelfSlot,
    file: Blob,
    filename: string,
    mode: KycCaptureMode,
  ) => {
    const formData = new FormData()
    formData.append('file', file, filename)
    formData.append('capture_mode', mode)
    return api
      .put<KycSelfStatus>(
        `/api/compliance/kyc/self/${verificationId}/files/${slot}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => r.data)
  },

  /**
   * Hands the self identification over to the human review.
   *
   * 422 `KYC_INCOMPLETE` lists the missing slots, 422 `KYC_DOCUMENT_EXPIRED`
   * means the document has expired, 409 that it was submitted already.
   */
  submitSelfIdent: (verificationId: number, data: KycSelfSubmitRequest) =>
    api
      .post<KycSelfStatus>(`/api/compliance/kyc/self/${verificationId}/submit`, data)
      .then((r) => r.data),

  /**
   * Starts the automatic reading of the document pictures (Issue #1165).
   *
   * Called as soon as front and back (or the PDF copy) are stored, so the
   * reading runs while the selfie is still being taken. The result arrives
   * through the `analysis` section of the status; a new call after a retake
   * reads the replaced page again.
   */
  analyzeSelfIdent: (verificationId: number) =>
    api
      .post<KycSelfStatus>(`/api/compliance/kyc/self/${verificationId}/analyze`)
      .then((r) => r.data),

  /** Current state of a self identification (which files are on file). */
  getSelfIdentStatus: (verificationId: number) =>
    api.get<KycSelfStatus>(`/api/compliance/kyc/self/${verificationId}`).then((r) => r.data),

  /**
   * Short-lived code for continuing on the phone (Issue #1071).
   *
   * The returned `handoff_url` becomes the QR code on the desktop; it is valid
   * for 15 minutes, may be redeemed once and stays bound to this very
   * identification.
   */
  createKycHandoff: (verificationId: number) =>
    api
      .post<KycHandoffResponse>(`/api/compliance/kyc/self/${verificationId}/handoff`)
      .then((r) => r.data),

  /** Self-declaration for supervised/listed/public-law counterparties. */
  declareRegulated: (data: RegulatedDeclarationRequest) =>
    api
      .post<RegulatedDeclarationResponse>('/api/compliance/kyc/regulated-declaration', data)
      .then((r) => r.data),

  /** Stock and last import per supervisory list plus the match counters. */
  getRegulatedStatus: () =>
    api.get<RegulatedStatusResponse>('/api/compliance/kyc/regulated/status').then((r) => r.data),

  /** One page of the review queue, filtered by match state. */
  getRegulatedMatches: (status: RegulatedMatchStatusValue, limit = 50, offset = 0) =>
    api
      .get<RegulatedMatchListResponse>('/api/compliance/kyc/regulated/matches', {
        params: { status, limit, offset },
      })
      .then((r) => r.data),

  /** Re-imports the configured lists (admin); idempotent, per-list errors. */
  runRegulatedImport: () =>
    api
      .post<RegulatedImportResponse>('/api/compliance/kyc/regulated/import', {})
      .then((r) => r.data),

  /** Matches all companies against the lists and fills the review queue. */
  runRegulatedMatching: () =>
    api
      .post<RegulatedMatchRunResponse>('/api/compliance/kyc/regulated/matches/run')
      .then((r) => r.data),

  /** Confirms a match — sets the supervision status of the company. */
  confirmRegulatedMatch: (data: RegulatedConfirmRequest) =>
    api
      .post<RegulatedConfirmResponse>('/api/compliance/kyc/regulated/confirm', data)
      .then((r) => r.data),

  /** Rejects a match; it is not proposed again on the next run. */
  rejectRegulatedMatch: (matchId: number) =>
    api
      .post<RegulatedMatch>(`/api/compliance/kyc/regulated/matches/${matchId}/reject`)
      .then((r) => r.data),

  /**
   * Starts a check run for the current user (Ident, Zefix, signature
   * authority, SECO, transparency register). Runs in the background — the
   * response already contains all steps in `PENDING`.
   */
  runKycChecks: () =>
    api.post<KycCheckRun>('/api/compliance/kyc/checks/run').then((r) => r.data),

  /** Current state of a check run including every single step. */
  getKycCheckRun: (runId: number) =>
    api.get<KycCheckRun>(`/api/compliance/kyc/checks/${runId}`).then((r) => r.data),

  /**
   * Completes a stub ident session (staging/dev only — the backend answers
   * with 404 in production).
   */
  completeStubKyc: (providerRef: string) =>
    api
      .post<KycCallbackResponse>(
        `/api/compliance/kyc/stub/${encodeURIComponent(providerRef)}/complete`,
        {},
      )
      .then((r) => r.data),

  /**
   * Review queue of submitted self-identifications, oldest first.
   *
   * `open` = OPEN + FIRST_APPROVED, `all` = every submitted case. Only
   * admin/GwG officers get an answer, everyone else a 403.
   */
  listKycReviews: (state: 'open' | 'all' = 'open') =>
    api
      .get<KycReviewItem[]>('/api/compliance/kyc/review', { params: { state } })
      .then((r) => r.data),

  /** One submitted identification in detail. */
  getKycReview: (documentId: number) =>
    api.get<KycReviewItem>(`/api/compliance/kyc/review/${documentId}`).then((r) => r.data),

  /**
   * Image or PDF of one slot.
   *
   * Needs the login header, so the file cannot be used as a plain `src` — the
   * caller turns the blob into an object URL and revokes it again.
   */
  getKycReviewFile: (documentId: number, slot: KycReviewFileSlot) =>
    api
      .get<Blob>(`/api/compliance/kyc/review/${documentId}/files/${slot}`, {
        responseType: 'blob',
      })
      .then((r) => r.data),

  /** Approves or rejects an identification; rejection requires a reason. */
  decideKycReview: (documentId: number, data: KycReviewDecisionRequest) =>
    api
      .post<KycReviewItem>(`/api/compliance/kyc/review/${documentId}/decision`, data)
      .then((r) => r.data),

  /**
   * Starts reading the document with our own AI again.
   *
   * Answers 409 when the feature is switched off or a run is still going; the
   * result is only a hint — the release stays a human decision.
   */
  extractKycReview: (documentId: number) =>
    api
      .post<KycReviewItem>(`/api/compliance/kyc/review/${documentId}/extract`)
      .then((r) => r.data),
}

export default complianceApi
